import { MorningNightProduct, RegimenClass } from '@cureskin/api-client/src/server-api';
import { Regimen as LocalRegimen } from 'client/app/app-constant-types';
import { REGIMEN_ROUTE_PATHS, REGIMEN_TAB_TYPES, REGIMEN_VIEW_STATES } from './regimen.constants';

/** RegimenClass plus the OTHERS sentinel used for the add-on instructions tab. */
export type RegimenTabClass = RegimenClass | typeof REGIMEN_TAB_TYPES.OTHERS;

/**
 * All possible view states in the regimen state machine.
 * Derived from REGIMEN_VIEW_STATES — runtime values and the type stay in lockstep.
 * Maps 1:1 with experiment variant keys and route paths.
 */
export type RegimenViewState = typeof REGIMEN_VIEW_STATES[keyof typeof REGIMEN_VIEW_STATES];

/**
 * Experiment variant flags for regimen route migration.
 * Each key corresponds to a RegimenViewState.
 * true = view is migrated to a route component; false/absent = still in monolith.
 */
export type RegimenRouteVariant = Partial<Record<RegimenViewState, boolean>>;

/**
 * Morning/night product after processRegimen() — `instruction` is split from string to string[].
 */
export interface ProcessedRegimenProduct extends Omit<MorningNightProduct, 'instruction'> {
  instruction?: string | string[];
}

/**
 * Regimen after processRegimen() — includes computed pricing and display flags.
 * Extends the local Regimen type (used by NgRx store and RegimenService).
 */
export interface ProcessedRegimen extends Omit<LocalRegimen, 'morning' | 'night'> {
  objectId?: string;
  class?: RegimenClass;
  active?: boolean;
  orderPlaced?: boolean;
  activeFrom?: { iso: string };
  fixedPrice?: number;
  fixedPriceMRP?: number;
  regimenId?: string;

  showConsultationView?: boolean;
  showProductSummary?: boolean;
  showPriceDetail?: boolean;
  regimenPreparation?: boolean;

  totalMRP?: number;
  totalSP?: number;
  discount?: number;

  morning?: ProcessedRegimenProduct[];
  night?: ProcessedRegimenProduct[];
}

/** Tab change event emitted by toolbar-tabs component */
export interface TabChangeEvent {
  index: number;
  type: string;
}

/** Tab configuration data for toolbar-tabs component */
export interface TabData {
  count: number;
  types: string[];
  selectedTab: number;
}

/** Options for getDisplayState() */
export interface DisplayStateOptions {
  consultationSession?: { get(key: string): unknown } | null;
}

/** Static mapping from view state to route path */
export const VIEW_TO_ROUTE_MAP: Readonly<Record<RegimenViewState, string>> = {
  [REGIMEN_VIEW_STATES.INSTRUCTION]: REGIMEN_ROUTE_PATHS.INSTRUCTIONS,
  [REGIMEN_VIEW_STATES.EMPTY]: REGIMEN_ROUTE_PATHS.EMPTY,
  [REGIMEN_VIEW_STATES.UNPAID]: REGIMEN_ROUTE_PATHS.UNPAID,
  [REGIMEN_VIEW_STATES.PREPARATION]: REGIMEN_ROUTE_PATHS.PREPARATION,
  [REGIMEN_VIEW_STATES.CONSULTATION]: REGIMEN_ROUTE_PATHS.CONSULTATION,
  [REGIMEN_VIEW_STATES.CHAT]: REGIMEN_ROUTE_PATHS.CHAT,
  [REGIMEN_VIEW_STATES.ADDON_INSTRUCTIONS]: REGIMEN_ROUTE_PATHS.ADDON_INSTRUCTIONS,
} as const;
